import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildFilesUrl } from '../config/api';

interface Step {
  number: number;
  description: string;
  status: 'pending' | 'running' | 'success' | 'error';
  details?: string;
  timestamp?: number;
}

export const FileUploadDebugger: React.FC = () => {
  const { token } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isPublic, setIsPublic] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const initialSteps: Step[] = [
    { number: 1, description: "User clicks 'Choose File'", status: 'pending' },
    { number: 2, description: "File selected and shown in UI", status: 'pending' },
    { number: 3, description: "User clicks 'Upload to R2'", status: 'pending' },
    { number: 4, description: "Validate file exists", status: 'pending' },
    { number: 5, description: "Check auth token exists", status: 'pending' },
    { number: 6, description: "Create FormData object", status: 'pending' },
    { number: 7, description: "Add file to FormData", status: 'pending' },
    { number: 8, description: "Add 'public' flag to FormData", status: 'pending' },
    { number: 9, description: "Build upload URL", status: 'pending' },
    { number: 10, description: "Send POST request to Files Worker", status: 'pending' },
    { number: 11, description: "Files Worker receives request", status: 'pending' },
    { number: 12, description: "Worker checks Authorization header", status: 'pending' },
    { number: 13, description: "Worker decodes JWT token", status: 'pending' },
    { number: 14, description: "Worker parses FormData", status: 'pending' },
    { number: 15, description: "Worker uploads to R2", status: 'pending' },
  ];

  const updateStep = (stepNumber: number, status: 'running' | 'success' | 'error', details?: string) => {
    setSteps(prev => prev.map(step =>
      step.number === stepNumber
        ? { ...step, status, details, timestamp: Date.now() }
        : step
    ));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setSteps(initialSteps);
      updateStep(1, 'success', `Clicked at ${new Date().toLocaleTimeString()}`);
      setTimeout(() => {
        updateStep(2, 'success', `File: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
      }, 100);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setIsUploading(true);

    // Step 3: Click Upload
    updateStep(3, 'success', 'Button clicked');
    await delay(200);

    // Step 4: Validate file
    updateStep(4, 'running', 'Checking file...');
    await delay(300);
    updateStep(4, 'success', `File valid: ${selectedFile.name}`);

    // Step 5: Check token
    updateStep(5, 'running', 'Checking authentication...');
    await delay(300);
    if (!token) {
      updateStep(5, 'error', 'NO TOKEN FOUND!');
      setIsUploading(false);
      return;
    }
    updateStep(5, 'success', `Token: ${token.substring(0, 20)}...`);

    // Step 6: Create FormData
    updateStep(6, 'running', 'Creating FormData...');
    await delay(200);
    const formData = new FormData();
    updateStep(6, 'success', 'FormData created');

    // Step 7: Add file
    updateStep(7, 'running', 'Adding file to FormData...');
    await delay(200);
    formData.append('file', selectedFile);
    updateStep(7, 'success', `Added: ${selectedFile.name}`);

    // Step 8: Add public flag
    updateStep(8, 'running', 'Adding public flag...');
    await delay(200);
    formData.append('public', isPublic.toString());
    updateStep(8, 'success', `Public: ${isPublic}`);

    // Step 9: Build URL
    updateStep(9, 'running', 'Building upload URL...');
    await delay(200);
    const uploadUrl = buildFilesUrl('/files');
    updateStep(9, 'success', `URL: ${uploadUrl}`);

    // Step 10: Send request
    updateStep(10, 'running', 'Sending POST request...');

    try {
      console.log('🚀 Sending upload request to:', uploadUrl);
      console.log('📦 FormData contents:', {
        file: selectedFile.name,
        public: isPublic
      });
      console.log('🔑 Auth header:', `Bearer ${token.substring(0, 20)}...`);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      updateStep(10, 'success', `Response: ${response.status} ${response.statusText}`);

      // Steps 11-15 happen on the server
      if (response.ok) {
        updateStep(11, 'success', 'Worker received request');
        updateStep(12, 'success', 'Auth header verified');
        updateStep(13, 'success', 'JWT decoded');
        updateStep(14, 'success', 'FormData parsed');
        updateStep(15, 'success', 'FILE UPLOADED TO R2! 🎉');

        const data = await response.json();
        console.log('✅ Upload successful:', data);
        alert(`Success! File key: ${data.file?.key}`);
      } else {
        const errorData = await response.json();
        console.error('❌ Upload failed:', errorData);

        // Try to determine which step failed
        if (errorData.error?.includes('authorization')) {
          updateStep(11, 'success', 'Worker received request');
          updateStep(12, 'error', errorData.error);
        } else if (errorData.error?.includes('authentication')) {
          updateStep(11, 'success', 'Worker received request');
          updateStep(12, 'success', 'Auth header found');
          updateStep(13, 'error', errorData.error);
        } else {
          updateStep(11, 'error', `Server error: ${errorData.error}`);
        }
      }
    } catch (error) {
      console.error('💥 Network error:', error);
      updateStep(10, 'error', `Network error: ${error instanceof Error ? error.message : 'Unknown'}`);
    }

    setIsUploading(false);
  };

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'running': return 'text-blue-600 bg-blue-50 animate-pulse';
      default: return 'text-gray-400 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'running': return '⚡';
      default: return '⏳';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-6">🔍 File Upload Debugger</h2>

      {/* File Selection */}
      <div className="mb-6 p-4 bg-gray-50 rounded">
        <div className="flex items-center gap-4 mb-4">
          <input
            type="file"
            onChange={handleFileSelect}
            className="block text-sm text-gray-900
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-600 file:text-white
              hover:file:bg-blue-700
              file:cursor-pointer"
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Public file</span>
          </label>
        </div>

        {selectedFile && (
          <div className="text-sm text-gray-600">
            Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(2)} KB)
          </div>
        )}
      </div>

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={!selectedFile || isUploading}
        className={`mb-6 px-6 py-3 rounded-lg font-bold transition-all ${
          !selectedFile || isUploading
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-green-600 hover:bg-green-700 text-white'
        }`}
      >
        {isUploading ? '⚡ Uploading...' : '🚀 Upload to R2'}
      </button>

      {/* Steps Timeline */}
      <div className="space-y-2">
        {steps.map((step) => (
          <div
            key={step.number}
            className={`flex items-start gap-3 p-3 rounded-lg transition-all ${getStatusColor(step.status)}`}
          >
            <div className="flex-shrink-0 w-8 text-center font-bold">
              {step.number}
            </div>
            <div className="flex-shrink-0 w-6">
              {getStatusIcon(step.status)}
            </div>
            <div className="flex-grow">
              <div className="font-medium">{step.description}</div>
              {step.details && (
                <div className="text-sm mt-1 opacity-75">{step.details}</div>
              )}
            </div>
            {step.timestamp && (
              <div className="text-xs opacity-50">
                +{((step.timestamp - (steps[0]?.timestamp || 0)) / 1000).toFixed(2)}s
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Console Output */}
      <div className="mt-6 p-4 bg-black text-green-400 rounded font-mono text-xs">
        <div>Console Output:</div>
        <div>Check browser DevTools for detailed logs</div>
        {token && <div>Token: ✓ Present</div>}
        {selectedFile && <div>File: ✓ {selectedFile.name}</div>}
        <div>Files URL: {buildFilesUrl('/files')}</div>
      </div>
    </div>
  );
};